import { useIntl } from 'react-intl';
import { ruRU } from '@mui/x-data-grid-pro';
// eslint-disable-next-line import/no-duplicates
import kk from 'date-fns/locale/kk';
// eslint-disable-next-line import/no-duplicates
import ru from 'date-fns/locale/ru';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import moment from 'moment/min/moment-with-locales';
import { useTranslation as useTranslationNext } from 'react-i18next';
import { messages } from '../../messages';
import { LOCALES, PREFIXES } from '../../locales';
import { kzKZ } from '../../dataGrid/kzKZ';

/**
 * Обёртка над react-i18next + react-intl.
 *
 * `ns` — namespace(ы) словаря i18next, на которые подписывается компонент.
 * Указывать его ОБЯЗАТЕЛЬНО для всего, что лежит не в 'common': словари грузятся асинхронно
 * (i18next-resources-to-backend в shared/utils/i18next/i18next.ts), и без имени namespace
 * компонент не подписан на его загрузку — первый рендер отдаёт сырой ключ
 * («residentRequests:cabinet.title»), а перерисовки после подгрузки словаря не происходит.
 * 'common' добавляем всегда: на нём висят общие подписи, в т.ч. локаль DataGrid ниже.
 *
 * Параметр необязательный — все прежние вызовы useTranslation() работают как раньше.
 */
export const useTranslation = (ns?: string | string[]) => {
  const intl = useIntl();
  const namespaces = ns ? ['common', ...(Array.isArray(ns) ? ns : [ns])] : undefined;
  const { t } = useTranslationNext(namespaces);

  const translateToken = (id: string, fallbackMessage: string | null = null) => {
    if (!id) {
      return fallbackMessage || '';
    }
    const defaultMessage = fallbackMessage || messages[intl.defaultLocale][id];
    return intl.formatMessage({ id, defaultMessage });
  };

  const fieldWithPrefix = (value: Record<string, any>, fieldWithoutPrefix: string) => {
    const options = PREFIXES[intl.locale]
      .map((prefix) => value[`${fieldWithoutPrefix}${prefix}`])
      .filter((option) => option !== undefined);

    if (options.length > 0) {
      return options[0];
    }

    return value[`${fieldWithoutPrefix}${PREFIXES[LOCALES.RU][2]}`];
  };

  const addPrefix = (value: string) => `${value}${PREFIXES[intl.locale][2]}`;

  const getGridLocale = () => {
    if (intl.locale === LOCALES.KZ) return kzKZ;

    return ruRU.components.MuiDataGrid.defaultProps.localeText;
  };

  const getDateLocale = () => {
    if (intl.locale === LOCALES.KZ) return kk;

    return ru;
  };

  const getGridPaginationLocaleProps = () => {
    const props = {
      pagination: {
        labelRowsPerPage: t('common:dataGrid.rowsPerPage'),
        labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
          `${from}–${to} ${t('common:dataGrid.of')} ${count}`
      }
    };

    return props;
  };

  const getLocale = () => {
    if (intl.locale === LOCALES.KZ) return 'kz';

    return 'ru';
  };

  const getMoment = () => {
    const localMoment = moment();
    if (intl.locale === LOCALES.KZ) return localMoment.locale('kk');
    return localMoment.locale('ru');
  };

  return {
    translateToken,
    fieldWithPrefix,
    getGridLocale,
    getGridPaginationLocaleProps,
    addPrefix,
    getDateLocale,
    getLocale,
    getMoment,
    t,
    intl
  };
};
